import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useRef } from 'react'

import { useNinetailed } from '@ninetailed/experience.js-react'

import SearchModal from '@/components/search/search-modal'
import FloatingChat from '@/components/ai-chat/floating-chat'
import FloatingWideAskAi from './ai-chat/floating-wide-ask-ai'
import useAskAiSettings from './ai-chat/use-ask-ai-settings'

import { getOs } from '@/utils/user-info'
import { useLocation } from '@/utils/url'
import { removeLocalePrefix, useTranslations } from '@/utils/locale'

export type ChatOpenTrackingOptions = {
  agent?: string
  pagePath?: string
  chatSource?: string
}

type SearchContextType = {
  aiQuery: string
  setAiQuery: React.Dispatch<React.SetStateAction<string>>
  isChatOpen: boolean
  setIsChatOpen: React.Dispatch<React.SetStateAction<boolean>>
  isChatFullscreen: boolean
  setIsChatFullscreen: React.Dispatch<React.SetStateAction<boolean>>
  isModalOpen: boolean
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  isAiLoading: boolean
  setIsAiLoading: React.Dispatch<React.SetStateAction<boolean>>
  isAskAiEnabled: boolean
  isFloatingChatEnabled: boolean
  isNavDrawerOpen: boolean
  setIsNavDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>
  isSalesContext: boolean
  askAiAgent: 'help' | 'sales'
  askAi: (prompt: string) => void
  askAiAboutPage: (url?: string) => void
  trackChatOpen: (ui: string, options?: ChatOpenTrackingOptions) => void
}

/**
 * `useNinetailed` throws outside a NinetailedProvider (e.g. in Storybook), so fall back to no tracking there.
 */
const useOptionalNinetailed = () => {
  try {
    return useNinetailed()
  } catch {
    return undefined
  }
}

const SearchContext = createContext<SearchContextType | undefined>(undefined)

type SearchProviderProps = {
  children: ReactNode
  disableFloatingChat?: boolean
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children, disableFloatingChat = false }) => {
  const [aiQuery, setAiQuery] = useState<string>('')
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false)
  const [isChatFullscreen, setIsChatFullscreen] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isAiLoading, setIsAiLoading] = useState(false)
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false)
  const { t } = useTranslations()
  const ninetailed = useOptionalNinetailed()
  const { path, pathWithoutLocale: cleanPath } = useLocation()
  const { enabled: isAskAiEnabled, floatingChat: floatingChatSettings, ntExperience } = useAskAiSettings()

  // Interaction-gated Ask AI experiment exposure: the variant is resolved silently on load, and the user is
  // only counted against baseline/variant once they open the chat. Fired once per session via this ref; the
  // anchor is a layout-neutral element Ninetailed uses purely as a dedup key.
  const impressionAnchorRef = useRef<HTMLSpanElement>(null)
  const hasTrackedExperienceExposure = useRef(false)

  const isSalesContext = useMemo(() => {
    return !cleanPath.startsWith('/help/')
  }, [cleanPath])

  const hasFloatingChatLoadedBefore = useRef(false)
  const isFloatingChatEnabled = useMemo(() => {
    if (!isAskAiEnabled || disableFloatingChat || cleanPath === '/ask-ai/') {
      return false
    }
    return (
      hasFloatingChatLoadedBefore.current ||
      // Trigger points: Pages where the floating chat should be enabled
      cleanPath === '/' ||
      cleanPath.startsWith('/products/') ||
      cleanPath.startsWith('/pricing/') ||
      cleanPath.startsWith('/pricing-all/') ||
      cleanPath.startsWith('/solutions/') ||
      cleanPath.startsWith('/help/') ||
      [
        '/bitwarden-alternatives/',
        '/bitwarden-vs-1password/',
        '/bitwarden-vs-lastpass/',
        '/bitwarden-vs-dashlane/',
        '/bitwarden-vs-keeper/',
      ].includes(cleanPath)
    )
  }, [cleanPath, isAskAiEnabled, disableFloatingChat])
  useEffect(() => {
    if (isFloatingChatEnabled) {
      hasFloatingChatLoadedBefore.current = true
    }
  }, [isFloatingChatEnabled])

  // Determine if the current page should only show the floating chat button (no wide chat)
  const isButtonOnlyPage = useMemo(() => {
    return (
      cleanPath.startsWith('/pricing/') ||
      cleanPath.startsWith('/pricing-all/') ||
      cleanPath.startsWith('/blog/') ||
      cleanPath.startsWith('/resources/') ||
      cleanPath.startsWith('/events/') ||
      cleanPath === '/case-studies/'
    )
  }, [cleanPath, isFloatingChatEnabled])

  const askAiAgent = isSalesContext ? 'sales' : 'help'

  const askAi = (prompt: string) => {
    setAiQuery(prompt)
    setIsChatOpen(true)
  }

  const askAiAboutPage = (url = path) => {
    const prompt = t('Look up the document with this URL: ${url} and answer my questions about it.').replace(
      '${url}',
      url
    )
    askAi(prompt)
  }

  const trackChatOpen = (ui: string, options: ChatOpenTrackingOptions = {}) => {
    const props = {
      ...options,
      agent: options.agent ?? askAiAgent,
      pagePath: options.pagePath ?? removeLocalePrefix(window.location.pathname),
      ui,
    }

    if (typeof window.plausible === 'function') {
      window.plausible('AI Chat Opened', { props })
    }

    ninetailed?.track('ai-chat-opened', props)

    // Record experiment exposure only now that the user has interacted. `trackComponentView` emits the same
    // `HAS_SEEN_ELEMENT` event the nav uses; Ninetailed drops it unless both an experience and an audience are
    // present, so this never logs phantom impressions when no experiment is running.
    if (
      ninetailed &&
      !hasTrackedExperienceExposure.current &&
      ntExperience?.status === 'success' &&
      ntExperience.experience &&
      ntExperience.audience &&
      ntExperience.variant &&
      impressionAnchorRef.current
    ) {
      hasTrackedExperienceExposure.current = true
      ninetailed.trackComponentView({
        element: impressionAnchorRef.current,
        experience: ntExperience.experience,
        audience: ntExperience.audience,
        variant: { id: ntExperience.variant.id },
        variantIndex: ntExperience.variantIndex,
      })
    }
  }

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = getOs() === 'MacOS'
      const modKey = isMac ? e.metaKey : e.ctrlKey

      if (modKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setIsModalOpen((prev) => !prev)
        setIsChatOpen(false)
        setIsChatFullscreen(false)
      }

      if (modKey && e.key.toLowerCase() === 'i') {
        e.preventDefault()
        setIsChatOpen((prev) => !prev)
        setIsModalOpen(false)
      }

      if (e.key === 'Escape') {
        setIsModalOpen(false)
        setIsChatOpen(false)
        setIsChatFullscreen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const value: SearchContextType = {
    aiQuery,
    setAiQuery,
    isChatOpen,
    setIsChatOpen,
    isChatFullscreen,
    setIsChatFullscreen,
    isModalOpen,
    setIsModalOpen,
    isAiLoading,
    setIsAiLoading,
    isAskAiEnabled,
    isFloatingChatEnabled,
    isNavDrawerOpen,
    setIsNavDrawerOpen,
    isSalesContext,
    askAiAgent,
    askAi,
    askAiAboutPage,
    trackChatOpen,
  }

  let isFloatingButtonVisible = false
  let isFloatingWideVisible = false
  if (isFloatingChatEnabled) {
    if (isButtonOnlyPage) {
      isFloatingButtonVisible = true
    } else if (isSalesContext) {
      isFloatingButtonVisible = floatingChatSettings?.sales === 'button'
      isFloatingWideVisible = floatingChatSettings?.sales === 'wide'
    } else {
      isFloatingButtonVisible = floatingChatSettings?.help === 'button'
      isFloatingWideVisible = floatingChatSettings?.help === 'wide'
    }
  }

  return (
    <SearchContext.Provider value={value}>
      {children}
      <span ref={impressionAnchorRef} style={{ display: 'contents' }} aria-hidden="true" />
      <SearchModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      {isFloatingButtonVisible && <FloatingChat isOpen={isChatOpen} setIsOpen={setIsChatOpen} />}
      {isFloatingWideVisible && <FloatingWideAskAi isOpen={isChatOpen} setIsOpen={setIsChatOpen} />}
    </SearchContext.Provider>
  )
}

export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext)
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider')
  }
  return context
}
